import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Toast } from "../components/ToastComponent";

export const useFetchData = ({ url, defaultQuery, sort = "-created_date" }) => {
  const DEFAULT_LIMIT = 10;

  const [data, setdata] = useState({
    results: {}, // will hold { stock_balance, warehouses }
    page: 1,
    limit: DEFAULT_LIMIT,
    totalCount: 0,
    query: {},
    sort,
    reloadTrigger: false,
  });

  // stringify objects so deps change only when content changes
  const defaultQueryKey = useMemo(
    () => JSON.stringify(defaultQuery ?? {}),
    [defaultQuery]
  );
  const queryKey = useMemo(
    () => JSON.stringify(data.query ?? {}),
    [data.query]
  );

  const setCurrentPage = (page) => {
    const totalPages = Math.max(
      1,
      Math.ceil((data.totalCount || 0) / (data.limit || DEFAULT_LIMIT))
    );
    const safe = Math.min(Math.max(page, 1), totalPages);
    setdata((prev) => ({ ...prev, page: safe }));
  };

  useEffect(() => {
    const parts = [];

    // Flatten defaultQuery once
    if (defaultQuery) {
      for (const key in defaultQuery) {
        const v = defaultQuery[key];
        if (v !== undefined && v !== "")
          parts.push(`${key}=${encodeURIComponent(v)}`);
      }
    }
    // Then dynamic query
    for (const key in data.query) {
      const v = data.query[key];
      if (v !== undefined && v !== "")
        parts.push(`${key}=${encodeURIComponent(v)}`);
    }

    const query = parts.length ? `&${parts.join("&")}` : "";
    const urlWithParams = `${url}?page=${data.page}&limit=${data.limit}&sort=${sort}&included=true${query}`;

    // optional: cancel in-flight fetches if deps change
    const controller = new AbortController();
    axios
      .get(urlWithParams, { withCredentials: true })
      // .get(urlWithParams, { withCredentials: true, signal: controller.signal })
      .then((res) => {
        const payload = res?.data?.data ?? {}; // { stock_balance, warehouses }
        const paginator = res?.data?.paginator ?? {}; // { totalItems, totalPages, currentPage, limit }

        setdata((prev) => ({
          ...prev,
          results: payload,
          totalCount: paginator.totalItems ?? prev.totalCount,
          page: paginator.currentPage ?? prev.page,
          limit: paginator.limit ?? prev.limit,
        }));
      })
      .catch((e) => {
        Toast.fire({ title: e?.response?.data?.message, icon: "error" });
        console.log(e?.response || e);
      });

    return () => controller.abort?.();
  }, [
    url,
    sort,
    defaultQueryKey,
    data.page,
    data.limit,
    queryKey,
    data.reloadTrigger,
  ]);

  const setQuery = (query) => {
    const clean = { ...query };
    for (const k in clean) if (!clean[k]) delete clean[k];

    const pageFromQuery = clean.page ? parseInt(clean.page, 10) : 1;
    delete clean.page; // avoid URL duplication

    setdata((prev) => ({
      ...prev,
      page: pageFromQuery,
      query: clean,
    }));
  };

  const reload = () => {
    setdata((prev) => ({ ...prev, reloadTrigger: !prev.reloadTrigger }));
  };

  return { data, setCurrentPage, setQuery, reload };
};
